// src/components/templates/TemplateFilterBar.jsx
//
// The bar above the template grid: a pill per category with its count, the
// search box, and the sort.
//
// **The search box moved here.** The prototype put it in its own top bar,
// which the gallery does not have — the app's Navbar stays — so it sits in the
// filter bar beside the controls it works with.
//
// Only categories that actually have a template are offered. The catalogue is
// seeded data, so a category can be emptied by retiring its last template, and
// a pill that always returns nothing looks like a broken page.
import React from 'react';
import { Box } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import { SP, FONT, BP, orderedCategoriesFor } from './templateTokens';
import { tokens } from '../../theme';

export const SORTS = [
  { id: 'popular', label: 'Sort: Popular' },
  { id: 'az', label: 'Sort: A–Z' },
  { id: 'short', label: 'Sort: Shortest first' },
  { id: 'long', label: 'Sort: Longest first' },
];

const controlSx = {
  fontFamily: FONT.sans,
  fontSize: 13,
  color: SP.black,
  background: SP.white,
  border: `1px solid ${SP.lineSoft}`,
  borderRadius: '8px',
  outline: 'none',
  '&:focus-visible, &:focus': { borderColor: SP.purpleMid },
};

export default function TemplateFilterBar({
  templates, active, onCategory, query, onQuery, sort, onSort,
}) {
  const options = [
    { id: 'all', label: 'All', count: templates.length },
    ...orderedCategoriesFor(templates).map((c) => ({
      ...c,
      count: templates.filter((t) => t.category === c.id).length,
    })),
  ];

  return (
    <Box
      data-testid="category-filter"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px',
        background: SP.white,
        p: '10px 12px',
        borderRadius: '12px',
        border: `1px solid ${SP.lineSoft}`,
        mb: '22px',
        [BP.panelFloats]: { flexDirection: 'column', alignItems: 'stretch' },
      }}
    >
      <Box
        role="group"
        aria-label="Template categories"
        // Wrapped rather than scrolled: the prototype's pills had the row to
        // themselves, and beside the search box a scrolling row cut categories
        // off at the edge with nothing to say there were more.
        sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}
      >
        {options.map((option) => {
          const isActive = option.id === active;
          return (
            <Box
              key={option.id}
              component="button"
              type="button"
              data-testid={`category-${option.id}`}
              aria-pressed={isActive}
              onClick={() => onCategory(option.id)}
              sx={{
                fontFamily: FONT.sans,
                flexShrink: 0,
                background: isActive ? SP.purple : SP.white,
                border: `1px solid ${isActive ? SP.purple : SP.lineSoft}`,
                color: isActive ? SP.white : SP.black,
                p: '7px 14px',
                borderRadius: '20px',
                fontSize: 13,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: `all ${tokens.motion.fast}ms ${tokens.motion.ease}`,
                '&:hover': isActive ? {} : { borderColor: SP.purpleSoft, color: SP.purple },
                '&:focus-visible': { outline: `2px solid ${SP.purpleMid}`, outlineOffset: '2px' },
              }}
            >
              {option.label}
              <Box component="span" sx={{ opacity: 0.6, ml: '4px', fontWeight: 400 }}>{option.count}</Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: '10px', flexShrink: 0, [BP.phone]: { flexDirection: 'column' } }}>
        <Box sx={{ position: 'relative', width: 220, [BP.panelFloats]: { flex: 1, width: 'auto' } }}>
          <SearchIcon sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 17, color: SP.gray }} />
          <Box
            component="input"
            type="search"
            data-testid="template-search"
            aria-label="Search templates"
            placeholder="Search templates..."
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            sx={{ ...controlSx, width: '100%', p: '7px 10px 7px 32px', '&::placeholder': { color: SP.gray } }}
          />
        </Box>
        <Box
          component="select"
          data-testid="template-sort"
          aria-label="Sort templates"
          value={sort}
          onChange={(e) => onSort(e.target.value)}
          sx={{ ...controlSx, p: '7px 10px', cursor: 'pointer' }}
        >
          {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </Box>
      </Box>
    </Box>
  );
}
